import { useState, useEffect } from "react";
import "../../assets/scss/notification/add-notification.scss";
import axiosConfig from "../../services/axiosConfig";
import setHeader from "../../components/Functions/setHeader";
import SweetAlert from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";

const AddNotification = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    msg_type: "info",
    read_status: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);

  // Message type options
  const messageTypes = [
    { value: "info", label: "Info" },
    { value: "success", label: "Success" },
    { value: "warning", label: "Warning" },
    { value: "error", label: "Error" },
  ];

  // Fetch notification data for editing
  useEffect(() => {
    if (isEditMode) {
      fetchNotificationData();
    }
  }, [id, isEditMode]);

  // Add this useEffect after your existing useEffect
  useEffect(() => {
  }, [formData]);

  const fetchNotificationData = async () => {
    setIsDataLoading(true);
    try {
      const response = await axiosConfig.get(`get-notification/${id}`, setHeader());
      if (!response.data.error) {
        const notificationData = response.data.data || response.data;
        
        const newFormData = {
          title: notificationData[0].title || "",
          message: notificationData[0].message || "",
          msg_type: notificationData[0].msg_type || "info",
          read_status: Boolean(notificationData[0].read_status),
        };
        
        setFormData(newFormData);
      } else {
        SweetAlert.fire({
          title: "Error",
          text: "Failed to fetch notification data",
          icon: "error",
        });
      }
    } catch (error) {
      console.error(error);
      SweetAlert.fire({
        title: "Error",
        text: "An error occurred while fetching notification data",
        icon: "error",
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    

    // Validation
    if (!formData.title.trim()) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Title is required",
        icon: "warning",
      });
      return;
    }

    if (!formData.message.trim()) {
      SweetAlert.fire({
        title: "Validation Error",
        text: "Message is required",
        icon: "warning",
      });
      return;
    }

    setIsLoading(true);

    const submitData = {
      title: formData.title,
      message: formData.message,
      msg_type: formData.msg_type,
      read_status: formData.read_status ? 1 : 0,
    };

    // For edit mode, add the ID
    if (isEditMode) {
      submitData.id = id;
    }

    // Debug: Log what we're sending

    const apiEndpoint = isEditMode ? "update-notification" : "save-notification";
    const successMessage = isEditMode
      ? "Notification updated successfully"
      : "Notification added successfully";

    axiosConfig
      .post(apiEndpoint, submitData, setHeader())
      .then((res) => {
        setIsLoading(false);
        
        if (!res.data.error) {
          SweetAlert.fire({
            title: "Success",
            text: res.data.messages || successMessage,
            icon: "success",
          });
          navigate('/pages/notification/notification-list')
          // Reset form after successful submission (only for add mode)
          if (!isEditMode) {
            handleReset();
          } else {
            // For edit mode, refresh the data to show updated info
            fetchNotificationData();
          }
        } else {
          SweetAlert.fire({
            title: "Oops!",
            text:
              res.data.messages ||
              `Failed to ${isEditMode ? "update" : "add"} notification. Try again!`,
            icon: "error",
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Backend error:', error);
        SweetAlert.fire({
          title: "Error",
          text: `An error occurred while ${
            isEditMode ? "updating" : "adding"
          } the notification`,
          icon: "error",
        });
      });
  };

  const handleReset = () => {
    if (isEditMode) {
      // For edit mode, reset to original data
      fetchNotificationData();
    } else {
      // For add mode, reset to empty form
      setFormData({
        title: "",
        message: "",
        msg_type: "info",
        read_status: false,
      });
    }
  };

  if (isDataLoading) {
    return (
      <div className="AddNotificationMainWrapper">
        <div className="notification-form-container">
          <div className="loading-state">Loading notification data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="AddNotificationMainWrapper">
      <div className="notification-form-container">
        <h2 className="form-title">
          {isEditMode ? "Edit Notification" : "Add Notification"}
        </h2>

        <form onSubmit={handleSubmit} className="notification-form">
          <div className="form-group">
            <label htmlFor="title" className="form-label">
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter notification title..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              className="form-textarea"
              placeholder="Enter notification message..."
              rows="4"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="msg_type" className="form-label">
              Message Type
            </label>
            <select
              id="msg_type"
              name="msg_type"
              value={formData.msg_type}
              onChange={handleInputChange}
              className="form-select"
              required
            >
              {messageTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="read_status"
                checked={formData.read_status}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="checkbox-text">Mark as Read</span>
            </label>
          </div> */}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? `${isEditMode ? "Updating" : "Submiting..."}`
                : `${isEditMode ? "Update" : "Submit"}`}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="btn btn-secondary"
              disabled={isLoading}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNotification;